import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axiosInstance from "../api/axiosInstance";
import { formatToDDMMYYYY, isCompletedStatus, resolveTaskTicketId } from "../utils/taskUtils";
import "./TaskDetailsModal.css";
import { Avatar, CircularProgress, Tooltip, IconButton, AvatarGroup, Box, Chip } from "@mui/material";
import {
  FaChevronDown,
  FaChevronUp,
  FaArrowRight,
  FaHistory,
  FaTimes,
  FaExpandAlt,
  FaGripVertical,
  FaEdit,
} from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import TaskAttachmentsPanel, { buildAttachmentList } from "./TaskAttachmentsPanel";
import UserAvatarTooltip from "./common/UserAvatarTooltip";
import TicketIdDisplay from "./common/TicketIdDisplay";
import InlineStatusDropdown from "./InlineStatusDropdown";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import "./TaskBoard.css";
import "./ModalStyles.css";
import ActivityTimelinePanel from "./ActivityTimelinePanel";
import WorkLogPanel from "./WorkLogPanel";
import { enrichTaskWithTimeMetrics, formatLoggedDuration, resolveTaskTimeMetrics } from "../utils/worklogUtils";
import {
  formatTimeSpent,
  resolveEstimatedSeconds,
  TIME_SPENT_HINT,
  TIME_SPENT_PLACEHOLDER,
  validateTimeSpentInput,
} from "../utils/timeUtils";


/* ─── constants (module-level, never re-created) ────────────────────────── */

const META_DASH = "—";

const AVATAR_COLORS = ["#3b82f6", "#10b981", "#8b5cf6", "#f59e0b", "#6366f1", "#ec4899"];

const STATUS_CONFIG = {
  completed: { bg: "#dcfce7", text: "#166534", border: "#bbf7d0", dot: "#16a34a" },
  pending: { bg: "#fef9c3", text: "#854d0e", border: "#fef08a", dot: "#ca8a04" },
  delegated: { bg: "#f3e8ff", text: "#6b21a8", border: "#e9d5ff", dot: "#9333ea" },
  wip: { bg: "#ffedd5", text: "#9a3412", border: "#fed7aa", dot: "#ea580c" },
  "in progress": { bg: "#ffedd5", text: "#9a3412", border: "#fed7aa", dot: "#ea580c" },
  "in-progress": { bg: "#ffedd5", text: "#9a3412", border: "#fed7aa", dot: "#ea580c" },
  revalidate: { bg: "#e0f2fe", text: "#0369a1", border: "#bae6fd", dot: "#0ea5e9" },
  reopen: { bg: "#fee2e2", text: "#991b1b", border: "#fecaca", dot: "#ef4444" },
};

const STATUS_FALLBACK = { bg: "#f3f4f6", text: "#374151", border: "#e5e7eb", dot: "#6b7280" };

const PRIORITY_CONFIG = {
  urgent: { color: "#dc2626", icon: "!!!" },
  high: { color: "#ea580c", icon: "!!" },
  medium: { color: "#3b82f6", icon: "!" },
  low: { color: "#6b7280", icon: "↓" },
};

const PRIORITY_FALLBACK = { color: "#6b7280", icon: "–" };



/* ─── pure helpers (stable references, no side-effects) ─────────────────── */

function formatDate(dateStr) {
  if (!dateStr) return "";
  const d = new Date(dateStr);
  return formatToDDMMYYYY(d);
}

function formatDateShort(dateStr) {
  if (!dateStr) return "";
  const d = new Date(dateStr);
  return formatToDDMMYYYY(d);
}

function formatTime(dateStr) {
  if (!dateStr) return "";
  const d = new Date(dateStr);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", hour12: true });
}

export function formatDateTime(dateStr) {
  if (!dateStr) return "";
  return `${formatDate(dateStr)} • ${formatTime(dateStr)}`;
}

function getInitials(name, email) {
  if (name) {
    const parts = name.trim().split(/\s+/).filter(Boolean);
    const letters = `${parts[0]?.[0] ?? ""}${parts[1]?.[0] ?? ""}`.trim();
    if (letters) return letters.toUpperCase();
  }
  if (email) return email.slice(0, 2).toUpperCase();
  return "NA";
}

function getAvatarColor(index) {
  return AVATAR_COLORS[index % AVATAR_COLORS.length];
}

export function getStatusConfig(status) {
  if (!status) return STATUS_FALLBACK;
  return STATUS_CONFIG[status.toLowerCase()] ?? STATUS_FALLBACK;
}

function getPriorityConfig(p) {
  if (!p) return PRIORITY_FALLBACK;
  return PRIORITY_CONFIG[p.toLowerCase()] ?? PRIORITY_FALLBACK;
}

/**
 * Normalise full name from various API shapes.
 * Returns empty string when nothing is found.
 */
export function resolveFullName(first, last) {
  return [first, last].filter(Boolean).join(" ").trim();
}

/**
 * Consistently check if a task is a "grouped" multi-assignee task.
 */
function checkIsGrouped(task) {
  if (!task) return false;
  return (
    task.is_grouped === 1 ||
    task.is_grouped === "1" ||
    task.is_grouped === true ||
    task.hasMultipleAssignees === true ||
    (Array.isArray(task.tasks) && task.tasks.length > 0) ||
    (Array.isArray(task.assignees) && task.assignees.length > 1)
  );
}

/* ─── memoised sub-components ───────────────────────────────────────────── */

/** Section heading with left blue bar accent */
const SectionTitle = React.memo(({ title }) => (
  <div className="tdm-section-title">
    <span className="tdm-section-bar" />
    <span className="tdm-section-label">{title}</span>
  </div>
));
SectionTitle.displayName = "SectionTitle";

/* ── SubtaskRow ─────────────────────────────────────────────────────────── */

const SubtaskRow = React.memo(({ st, onNavigate, onStatusSuccess, userId }) => {
  const stStatusCfg = useMemo(() => getStatusConfig(st.status), [st.status]);
  const firstName = useMemo(() => {
    return st.assignee?.firstName || st.assignee?.FirstName || st.assignee?.first_name || st?.user?.first_name;
  }, [st.assignee, st.user]);

  const lastName = useMemo(() => {
    return st.assignee?.lastName || st.assignee?.LastName || st.assignee?.last_name || st?.user?.last_name;
  }, [st.assignee, st.user]);

  const stAssigneeName = useMemo(
    () => resolveFullName(firstName, lastName) || "Unassigned",
    [firstName, lastName]
  );



  return (
    <div className="tdm-subtask-row">
      <div className="tdm-st-main">
        <FaGripVertical className="tdm-st-grip" />
        <TicketIdDisplay task={st} maxVisible={1} fontSize="0.85rem" />
        <span className="tdm-st-title" title={st.title}>{st.title}</span>
      </div>

      <div className="tdm-st-right">
        <UserAvatarTooltip
          firstName={firstName}
          lastName={lastName}
          email={st.assignee?.email || st.user?.email}
          displayName={stAssigneeName}
          avatarSx={{ width: 24, height: 24, fontSize: "0.65rem" }}
        />
        <div onClick={(e) => e.stopPropagation()}>
          <InlineStatusDropdown
            task={st}
            userId={userId}
            size="small"
            hideDelegateOption
            onSuccess={(msg, newStatus) => {
              onStatusSuccess?.(st.id, newStatus);
            }}
          />
        </div>
      </div>
    </div>
  );
});
SubtaskRow.displayName = "SubtaskRow";

/* ── DelegatedTaskItem ──────────────────────────────────────────────────── */

const DelegatedTaskItem = React.memo(({ d }) => {
  const cfg = useMemo(() => getStatusConfig(d.status), [d.status]);
  return (
    <div className="tdm-delegated-item">
      <Avatar sx={{ width: 26, height: 26, fontSize: "0.65rem", bgcolor: "#8b5cf6", flexShrink: 0 }}>
        {getInitials(d.assigneeName, d.assigneeEmail)}
      </Avatar>
      <div className="tdm-delegated-info">
        <span className="tdm-delegated-name">{d.assigneeName}</span>
        <span className="tdm-delegated-sub">{d.title}</span>
        <span className="tdm-delegated-dates">
          📅 {formatDate(d.createdAt)}{d.dueDate ? `  ⏰ ${formatDate(d.dueDate)}` : ""}
        </span>
      </div>
      <span className="tdm-delegated-status" style={{ background: cfg.bg, color: cfg.text }}>
        {d.status}
      </span>
    </div>
  );
});
DelegatedTaskItem.displayName = "DelegatedTaskItem";

/* ── TimelineEvent ──────────────────────────────────────────────────────── */

const TimelineEvent = React.memo(({ event, idx }) => {
  const cfg = useMemo(() => getStatusConfig(event.status), [event.status]);
  return (
    <div key={event.id || idx} className="tdm-timeline-item">
      <span className="tdm-timeline-dot" style={{ background: cfg.dot }} />
      <div className="tdm-timeline-content">
        <div className="tdm-timeline-row">
          <span className="tdm-timeline-status">{event.status}</span>
          <span className="tdm-timeline-by">by {event.createdBy}</span>
        </div>
        {event.remark && <p className="tdm-timeline-remark">{event.remark}</p>}
        <span className="tdm-timeline-date">{formatDateTime(event.createdAt)}</span>
      </div>
    </div>
  );
});
TimelineEvent.displayName = "TimelineEvent";

/* ── MemberCard ─────────────────────────────────────────────────────────── */

const MemberCard = React.memo(({ member, idx, isExpanded, onToggle }) => {
  const mStatus = useMemo(() => getStatusConfig(member.status), [member.status]);
  const filteredTimeline = useMemo(
    () =>
      (member.timeline ?? []).filter(
        (e) => !(e.status?.toLowerCase() || "").includes("released")
      ),
    [member.timeline]
  );

  return (
    <div className={`tdm-member-card${isExpanded ? " tdm-member-card--expanded" : ""}`}>
      <button className="tdm-member-row" onClick={onToggle}>
        <Avatar
          sx={{
            width: 34,
            height: 34,
            fontSize: "0.7rem",
            fontWeight: 700,
            bgcolor: getAvatarColor(idx),
            flexShrink: 0,
          }}
        >
          {getInitials(member.name, member.email)}
        </Avatar>
        <div className="tdm-member-info">
          <span className="tdm-member-name">{member.name}</span>
          <span className="tdm-member-email">{member.email || "No email"}</span>
        </div>
        <span className="tdm-member-status" style={{ color: mStatus.text }}>
          {member.status}
        </span>
        <span className="tdm-member-chevron">
          {isExpanded ? <FaChevronUp size={11} /> : <FaChevronDown size={11} />}
        </span>
      </button>

      {isExpanded && (
        <div className="tdm-member-expanded">
          {/* delegated tasks */}
          {member.delegatedTasks?.length > 0 && (
            <div className="tdm-delegated">
              <p className="tdm-delegated-title">
                <FaArrowRight size={9} style={{ marginRight: 6 }} />
                Delegated Assignments ({member.delegatedTasks.length})
              </p>
              {member.delegatedTasks.map((d) => (
                <DelegatedTaskItem key={d.id} d={d} />
              ))}
            </div>
          )}

          {/* per-member timeline */}
          <div className="tdm-timeline">
            <p className="tdm-timeline-title">
              <FaHistory size={10} style={{ marginRight: 6 }} />
              Activity Timeline
            </p>
            <div className="tdm-timeline-list">
              {filteredTimeline.length > 0 ? (
                filteredTimeline.map((event, eIdx) => (
                  <TimelineEvent key={event.id || eIdx} event={event} idx={eIdx} />
                ))
              ) : (
                <p className="tdm-timeline-empty">No activity recorded</p>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
});
MemberCard.displayName = "MemberCard";

/* ─── helper: build member list from raw API shapes ─────────────────────── */

function buildMemberList(details, rawTask) {
  const result = [];
  const delegationChildren = details?.delegationChildren ?? rawTask?.delegationChildren ?? [];
  const nestedTasks = details?.tasks || rawTask?.tasks || [];
  const isGrouped = details?.is_grouped || rawTask?.is_grouped || (Array.isArray(nestedTasks) && nestedTasks.length > 1);

  // 1. Grouped Task Logic: build from nested 'tasks' for granular status/timeline per member
  if (isGrouped && Array.isArray(nestedTasks) && nestedTasks.length > 0) {
    nestedTasks.forEach((t, idx) => {
      const u = t.user || t.assignee || {};
      const name = resolveFullName(u.first_name || u.FirstName || u.firstName, u.last_name || u.LastName || u.lastName) || "Unassigned";

      // Try to find status from task or fallback
      const status = t.status || rawTask?.status || "Pending";

      result.push({
        id: t.id || `task-${idx}`,
        name,
        email: u.email || null,
        status,
        createdAt: t.created_at || t.createdAt || rawTask?.created_at,
        dueDate: t.due_date || t.dueDate || rawTask?.due_date,
        ticket_id: t.ticket_id || t.id,
        timeline: t.timeline || t.actions || [],
        delegatedTasks: [], // Usually flattened here
      });
    });
    return result;
  }

  // 2. Legacy / Individual Task logic: build from 'assignees' array or 'assignee' object
  if (Array.isArray(details?.assignees) && details.assignees.length > 0) {
    details.assignees.forEach((assignee, index) => {
      const name =
        assignee.name ||
        resolveFullName(
          assignee.first_name || assignee.FirstName || assignee.firstName,
          assignee.last_name || assignee.LastName || assignee.lastName
        ) ||
        "Unassigned";

      const delegatedTasks = delegationChildren.map((child, idx) => ({
        id: child?.id || `delegated-${idx}`,
        assigneeName:
          resolveFullName(
            child?.assignee?.first_name || child?.assignee?.FirstName,
            child?.assignee?.last_name || child?.assignee?.LastName
          ) || "Unassigned",
        assigneeEmail: child?.assignee?.email || null,
        status: child?.status || "Pending",
        createdAt: child?.created_at,
        dueDate: child?.due_date,
        title: child?.title || "Delegated Task",
        remark: child?.description || child?.statusRemark || null,
      }));

      result.push({
        id: assignee.taskId || assignee.userId || assignee.id || `assignee-${index}`,
        name,
        email: assignee.email || null,
        status: assignee.status || rawTask?.status || "Pending",
        createdAt: assignee.createdAt || rawTask?.created_at,
        createdBy:
          rawTask?.given_by_name ||
          resolveFullName(
            rawTask?.givenBy?.first_name || rawTask?.givenBy?.FirstName || rawTask?.GivenByFirstName,
            rawTask?.givenBy?.last_name || rawTask?.givenBy?.LastName || rawTask?.GivenByLastName
          ) ||
          "N/A",
        dueDate: assignee.dueDate || rawTask?.due_date,
        delegatedTasks,
        timeline: assignee.timeline || [],
      });
    });
  } else if (rawTask) {
    const firstName =
      rawTask.assignee?.first_name || rawTask.assignee?.FirstName ||
      rawTask.FirstName || rawTask.first_name || rawTask.assignee_name || "";
    const lastName =
      rawTask.assignee?.last_name || rawTask.assignee?.LastName ||
      rawTask.LastName || rawTask.last_name || rawTask.assignee_last || "";

    const memberTimeline = [];
    if (rawTask.created_at) {
      memberTimeline.push({
        id: `assigned-${rawTask.id}`,
        status: "Assigned",
        remark: null,
        createdAt: rawTask.created_at,
        createdBy: rawTask.given_by_name || "System",
      });
    }
    if (Array.isArray(rawTask.actions)) {
      rawTask.actions.forEach((a) =>
        memberTimeline.push({
          id: a.id,
          status: a.status || a.action,
          remark: a.remark || a.remarks || null,
          createdAt: a.createdAt || a.created_at,
          createdBy: a.createdBy || "System",
        })
      );
    }

    result.push({
      id: rawTask.id || "main-assignee",
      name: resolveFullName(firstName, lastName) || "Unassigned",
      email:
        rawTask.assignee?.email || rawTask.email || rawTask.assignee_email || null,
      status: rawTask.status || "Pending",
      createdAt: rawTask.created_at,
      createdBy:
        rawTask.given_by_name ||
        resolveFullName(
          rawTask.givenBy?.first_name || rawTask.givenBy?.FirstName || rawTask?.GivenByFirstName,
          rawTask.givenBy?.last_name || rawTask.givenBy?.LastName || rawTask?.GivenByLastName
        ) ||
        "N/A",
      dueDate: rawTask.due_date,
      delegatedTasks: delegationChildren.map((child, idx) => ({
        id: child?.id || `delegated-${idx}`,
        assigneeName:
          resolveFullName(
            child?.assignee?.first_name || child?.assignee?.FirstName,
            child?.assignee?.last_name || child?.assignee?.LastName
          ) || "Unassigned",
        assigneeEmail: child?.assignee?.email || null,
        status: child?.status || "Pending",
        createdAt: child?.created_at,
        dueDate: child?.due_date,
        title: child?.title || "Delegated Task",
        remark: child?.description || child?.statusRemark || null,
      })),
      timeline: memberTimeline,
    });
  }

  return result;
}

/* ─── Main Component ─────────────────────────────────────────────────────── */

const TaskDetailsModal = React.memo(
  ({ open, onClose, task, showGivenBy = false, variant = "modal", onDelegateClick, onStatusSuccess, hideDelegateOption = false, externalWorkLogOpenSignal = 0 }) => {
    const navigate = useNavigate();
    const currentUser = useSelector((state) => state.auth.user);
    const currentUserId = useMemo(() => {
      if (!currentUser) return null;
      const id = currentUser.userId ?? currentUser.id ?? currentUser.user_id ?? currentUser.sub;
      return id != null ? String(id) : null;
    }, [currentUser]);

    const [details, setDetails] = useState(task || null);
    const [loading, setLoading] = useState(false);
    const [refreshing, setRefreshing] = useState(false);
    const [error, setError] = useState(null);
    const [selectedMemberId, setSelectedMemberId] = useState(null);
    const [subtasks, setSubtasks] = useState([]);
    const [loadingSubtasks, setLoadingSubtasks] = useState(false);
    const [timeline, setTimeline] = useState([]);
    const [loadingTimeline, setLoadingTimeline] = useState(false);
    const [activeTab, setActiveTab] = useState("timeline");
    const [workLogOpenSignal, setWorkLogOpenSignal] = useState(0);
    const [editingEstimated, setEditingEstimated] = useState(false);
    const [estimatedDraft, setEstimatedDraft] = useState("");
    const [estimatedError, setEstimatedError] = useState("");
    const [savingEstimated, setSavingEstimated] = useState(false);
    const estimatedInputRef = useRef(null);
    const processedWorkLogSignalRef = useRef(0);
    const combinedWorkLogSignal = workLogOpenSignal + externalWorkLogOpenSignal;

    useEffect(() => {
      if (!open) {
        processedWorkLogSignalRef.current = 0;
        setWorkLogOpenSignal(0);
        setActiveTab("timeline");
        return;
      }

      if (combinedWorkLogSignal <= processedWorkLogSignalRef.current) return;
      processedWorkLogSignalRef.current = combinedWorkLogSignal;
      setActiveTab("worklog");
    }, [open, combinedWorkLogSignal]);

    /* ── fetch-lock/persistence ───────────────────────────────────────── */
    const cancelledRef = useRef(false);
    const activeTaskRef = useRef({ taskId: null, controller: null, wasOpen: false });

    /* ── centralized fetch logic ─────────────────────────────────────── */
    const refreshTaskData = useCallback(async (id, isGroupedFlag, showLoader = true) => {
      if (!id || id.toString().startsWith("temp-")) return;

      if (showLoader) setRefreshing(true);
      const controller = new AbortController();
      activeTaskRef.current.controller = controller;

      try {
        const params = {};
        if (isGroupedFlag) params.is_grouped = 1;

        // 1. Fetch main details
        const detailsRes = await axiosInstance.get(`api/task/${id}`, {
          params,
          signal: controller.signal
        });
        const resolved = detailsRes.data?.data && typeof detailsRes.data.data === "object"
          ? detailsRes.data.data
          : detailsRes.data;

        if (activeTaskRef.current.taskId !== id) return;
        setDetails(resolved ? enrichTaskWithTimeMetrics(resolved) : null);

        // 2. Handle subtasks
        const effectivelyGrouped = checkIsGrouped(resolved) || isGroupedFlag;
        const taskList = Array.isArray(resolved?.tasks) ? resolved.tasks : [];

        // For deputed tasks, we want to call subtask API if we have fewer than 2 internal tasks
        const forceSubtaskFetch = hideDelegateOption && taskList.length < 2;

        if (effectivelyGrouped && taskList.length > 0 && !forceSubtaskFetch) {
          setSubtasks(taskList);
          setLoadingSubtasks(false);
        } else if (!effectivelyGrouped || forceSubtaskFetch) {
          // Standard/Individual task OR forced subtask fetch for deputed tasks
          try {
            const subRes = await axiosInstance.get(`api/task/${id}/subtasks`, { signal: controller.signal });
            if (activeTaskRef.current.taskId === id) {
              setSubtasks(subRes.data?.data?.items || []);
            }
          } catch (e) {
            if (activeTaskRef.current.taskId === id) setSubtasks([]);
          } finally {
            if (activeTaskRef.current.taskId === id) setLoadingSubtasks(false);
          }
        } else {
          // Grouped but empty (and not forced)
          setLoadingSubtasks(false);
        }

        // 3. Fetch activity timeline (async, don't block main render if possible)
        axiosInstance.get(`api/task/${id}/timeline`, { signal: controller.signal })
          .then(({ data }) => {
            if (activeTaskRef.current.taskId !== id) return;
            const items = data?.data?.data || data?.data?.items || (Array.isArray(data?.data) ? data.data : []);
            setTimeline(items);
          })
          .catch(() => { if (activeTaskRef.current.taskId === id) setTimeline([]); })
          .finally(() => { if (activeTaskRef.current.taskId === id) setLoadingTimeline(false); });

      } catch (err) {
        if (!controller.signal.aborted && activeTaskRef.current.taskId === id) {
          setError(err.message || "Failed to load task details");
        }
      } finally {
        if (activeTaskRef.current.taskId === id) {
          setLoading(false);
          if (showLoader) setRefreshing(false);
        }
      }
    }, [hideDelegateOption]);

    /* ── data-fetching effect ─────────────────────────────────────────── */
    useEffect(() => {
      const taskId = task?.id;

      // Case A: Modal/Drawer is closed or no task ID -> Cleanup and reset
      if ((!open && variant !== "full-page") || !taskId) {
        if (activeTaskRef.current.controller) {
          activeTaskRef.current.controller.abort();
        }
        activeTaskRef.current = { taskId: null, controller: null, wasOpen: false };

        setDetails(null);
        setSelectedMemberId(null);
        setError(null);
        setLoading(false);
        setSubtasks([]);
        setTimeline([]);
        return;
      }

      // Case B: Skip fetch if same task is already loaded and was open
      if (activeTaskRef.current.taskId === taskId && activeTaskRef.current.wasOpen) {
        return;
      }

      // Case C: Start new fetch
      if (activeTaskRef.current.controller) {
        activeTaskRef.current.controller.abort();
      }

      activeTaskRef.current = { taskId, controller: null, wasOpen: open };

      // Sync initial data from props to avoid empty state while loading
      setDetails(prev => (prev?.id === taskId ? prev : task));
      setLoading(true);
      setLoadingSubtasks(true);
      setLoadingTimeline(true);

      const isInitiallyGrouped = checkIsGrouped(task);

      refreshTaskData(taskId, isInitiallyGrouped, false);

      return () => {
        // We handle cleanup in the effect body above to allow data persistence between strict mode cycles
      };
    }, [open, task, variant, refreshTaskData]); // taskId check handled within the effect

    /* ── derived / memoised values ──────────────────────────────────── */

    const rawTask = details || task;

    const canEditEstimatedTime = useMemo(() => {
      if (!currentUserId) return false;

      const assignerId = String(
        details?.assigned_by ??
        rawTask?.assigned_by ??
        rawTask?.givenBy?.userId ??
        rawTask?.givenBy?.id ??
        rawTask?.GivenByUserId ??
        rawTask?.given_by ??
        ""
      );

      return assignerId !== "" && currentUserId === assignerId;
    }, [currentUserId, details, rawTask]);

    const estimatedSeconds = useMemo(
      () => resolveEstimatedSeconds(rawTask),
      [rawTask]
    );

    const { estimated, logged, percentage } = useMemo(() => {
      const estimatedMinutes = Math.round(estimatedSeconds / 60);
      const { loggedMinutes } = resolveTaskTimeMetrics(rawTask);
      const pct =
        estimatedSeconds > 0
          ? Math.min(100, Math.round((loggedMinutes / estimatedMinutes) * 100))
          : 0;
      return {
        estimated: estimatedMinutes,
        logged: loggedMinutes,
        percentage: pct,
      };
    }, [rawTask, estimatedSeconds]);

    const cancelEstimatedEdit = useCallback(() => {
      setEditingEstimated(false);
      setEstimatedDraft("");
      setEstimatedError("");
    }, []);

    const startEstimatedEdit = useCallback(() => {
      if (!canEditEstimatedTime) return;
      setEstimatedDraft(formatTimeSpent(estimatedSeconds));
      setEstimatedError("");
      setEditingEstimated(true);
    }, [estimatedSeconds, canEditEstimatedTime]);

    useEffect(() => {
      if (editingEstimated && estimatedInputRef.current) {
        estimatedInputRef.current.focus();
        estimatedInputRef.current.select();
      }
    }, [editingEstimated]);

    const saveEstimatedTime = useCallback(async () => {
      if (!rawTask?.id || savingEstimated || !canEditEstimatedTime) return;

      const parsed = validateTimeSpentInput(estimatedDraft);
      if (!parsed.valid) {
        setEstimatedError(parsed.error);
        return;
      }

      setSavingEstimated(true);
      setEstimatedError("");

      try {
        const isGrouped = checkIsGrouped(rawTask);
        const url = `api/task/${rawTask.id}${isGrouped ? "?is_grouped=1" : ""}`;

        await axiosInstance.put(url, { estimated_time: parsed.seconds });

        setDetails((prev) =>
          prev
            ? enrichTaskWithTimeMetrics({
              ...prev,
              estimated_time: parsed.seconds,
              estimatedTime: parsed.seconds,
            })
            : prev
        );
        cancelEstimatedEdit();
        toast.success("Estimated time updated successfully.");
      } catch (err) {
        const message =
          err?.response?.data?.message || err?.message || "Failed to update estimated time.";
        setEstimatedError(message);
        toast.error(message);
      } finally {
        setSavingEstimated(false);
      }
    }, [rawTask, savingEstimated, estimatedDraft, cancelEstimatedEdit, canEditEstimatedTime]);

    const handleEstimatedDraftChange = useCallback(
      (event) => {
        const value = event.target.value;
        setEstimatedDraft(value);
        if (estimatedError) {
          const parsed = validateTimeSpentInput(value);
          setEstimatedError(parsed.valid ? "" : parsed.error);
        }
      },
      [estimatedError]
    );

    const handleEstimatedKeyDown = useCallback(
      (event) => {
        if (event.key === "Enter") {
          event.preventDefault();
          saveEstimatedTime();
        } else if (event.key === "Escape") {
          event.preventDefault();
          cancelEstimatedEdit();
        }
      },
      [saveEstimatedTime, cancelEstimatedEdit]
    );

    useEffect(() => {
      cancelEstimatedEdit();
    }, [task?.id, cancelEstimatedEdit]);

    const handleEstimatedBlur = useCallback(() => {
      if (savingEstimated) return;

      const parsed = validateTimeSpentInput(estimatedDraft);
      if (!parsed.valid || parsed.seconds === estimatedSeconds) {
        cancelEstimatedEdit();
        return;
      }

      saveEstimatedTime();
    }, [
      savingEstimated,
      estimatedDraft,
      estimatedSeconds,
      cancelEstimatedEdit,
      saveEstimatedTime,
    ]);

    useEffect(() => {
      if (!canEditEstimatedTime) {
        cancelEstimatedEdit();
      }
    }, [canEditEstimatedTime, cancelEstimatedEdit]);

    const members = useMemo(
      () => buildMemberList(details, task || rawTask),
      [details, task, rawTask]
    );

    /**
     * Assignees to display in the ASSIGNEE chip row.
     * Priority:
     *   1. task.assignees  – deputed-tasks list payload (array)
     *   2. details.assignees – fetched detail-API array
     *   3. details.assignee  – fetched detail-API single object
     *   4. members           – built from detail-API response
     */
    const displayAssignees = useMemo(() => {
      // Priority: API details (fetched) should always be preferred over prop snapshot
      const rawDetailsArr = details?.assignees;
      if (Array.isArray(rawDetailsArr) && rawDetailsArr.length > 0) {
        return rawDetailsArr.map((a) => ({
          id: a.id || a.userId,
          firstName: a.first_name || a.firstName || "",
          lastName: a.last_name || a.lastName || "",
          email: a.email || null,
          displayName: resolveFullName(a.first_name || a.firstName, a.last_name || a.lastName) || a.name || "Unassigned",
        }));
      }

      const singleAssignee = details?.assignee;
      if (singleAssignee && typeof singleAssignee === "object") {
        return [
          {
            id: singleAssignee.id || singleAssignee.userId,
            firstName: singleAssignee.first_name || singleAssignee.firstName || "",
            lastName: singleAssignee.last_name || singleAssignee.lastName || "",
            email: singleAssignee.email || null,
            displayName: resolveFullName(singleAssignee.first_name || singleAssignee.firstName, singleAssignee.last_name || singleAssignee.lastName) || singleAssignee.name || "Unassigned",
          },
        ];
      }

      // Fallback to task prop if details haven't loaded yet
      const rawProp = task?.assignees;
      if (Array.isArray(rawProp) && rawProp.length > 0) {
        return rawProp.map((a) => ({
          id: a.id || a.userId,
          firstName: a.first_name || a.firstName || "",
          lastName: a.last_name || a.lastName || "",
          email: a.email || null,
          displayName: resolveFullName(a.first_name || a.firstName, a.last_name || a.lastName) || a.name || "Unassigned",
        }));
      }

      // Final fallback: derived members
      return members.map((m) => {
        const parts = (m.name || "").trim().split(/\s+/).filter(Boolean);
        return {
          id: m.id,
          firstName: parts[0] || "",
          lastName: parts.slice(1).join(" ") || "",
          email: m.email,
          displayName: m.name || "Unassigned",
        };
      });
    }, [details, task?.assignees, members]);



    const { taskTitle, taskId, statusCfg, prioCfg, assigneeName, assigneeEmail, createdByName, projectName, hasDueDate, givenBy, labels } =
      useMemo(() => {
        const title = rawTask?.title || "Task Details";
        const ticketRaw = rawTask?.ticket_id || rawTask?.ticketId || rawTask?.ticketNumber || rawTask?.ticket_number || null;
        const id = ticketRaw ? String(ticketRaw) : rawTask?.id ? `Task-${rawTask.id}` : "";
        const sCfg = getStatusConfig(rawTask?.status);
        const pCfg = getPriorityConfig(rawTask?.priority);
        const firstMember = members[0];
        const aName = firstMember?.name || META_DASH;
        const aEmail = firstMember?.email || null;
        const cbName =
          rawTask?.given_by_name ||
          resolveFullName(
            rawTask?.givenBy?.first_name || rawTask?.givenBy?.FirstName,
            rawTask?.givenBy?.last_name || rawTask?.givenBy?.LastName
          ) ||
          resolveFullName(rawTask?.GivenByFirstName, rawTask?.GivenByLastName) ||
          "";
        const pName = rawTask?.project?.name || rawTask?.project_name || null;
        const due = Boolean(rawTask?.due_date);

        const gBy = {
          firstName: rawTask?.givenBy?.first_name || rawTask?.givenBy?.FirstName || rawTask?.GivenByFirstName || "",
          lastName: rawTask?.givenBy?.last_name || rawTask?.givenBy?.LastName || rawTask?.GivenByLastName || "",
          email: rawTask?.givenBy?.email || null,
          displayName: cbName
        };

        return {
          taskTitle: title,
          taskId: id,
          statusCfg: sCfg,
          prioCfg: pCfg,
          assigneeName: aName,
          assigneeEmail: aEmail,
          createdByName: cbName,
          projectName: pName,
          hasDueDate: due,
          givenBy: gBy,
          labels: rawTask?.labels || [],
        };
      }, [rawTask, members]);

    const attachmentList = useMemo(
      () => buildAttachmentList(rawTask, details),
      [rawTask, details]
    );

    const canDeleteAttachments = useMemo(() => {
      if (!currentUser) return false;

      const role = String(currentUser.role || currentUser.Role || "").toLowerCase();
      if (role === "admin") return true;

      const curId = String(currentUser.id || currentUser.userId || currentUser.user_id);

      const isCreator = curId === String(details?.assigned_by || rawTask?.assigned_by || "");

      if (isCreator) return true;

      // If user is an assignee, they can't delete
      const isAssignee = displayAssignees.some(a => String(a.id || a.userId || a.user_id) === curId);
      if (isAssignee) return false;

      return false; // Default: Only creator or admin can delete
    }, [currentUser, details, rawTask, displayAssignees]);

    /* ── stable callbacks ─────────────────────────────────────────────── */

    const handleNavigateFullPage = useCallback(() => {
      const isGrouped = checkIsGrouped(rawTask);
      const params = new URLSearchParams();
      if (isGrouped) params.set("is_grouped", "1");
      if (hideDelegateOption) params.set("source", "deputed");
      const qs = params.toString();
      const url = `/task/${rawTask?.id}${qs ? `?${qs}` : ""}`;
      window.open(url, "_blank");
    }, [rawTask?.id, task?.tasks, task?.is_grouped, hideDelegateOption]);

    const handleSubtaskNavigate = useCallback(
      (id) => {
        // Instead of navigating in-place, we open sub-tasks in a new tab 
        // to keep the current context (modal/drawer) intact or just follow user preference.
        window.open(`/task/${id}`, "_blank");
      },
      []
    );


    const handleSubtaskStatusUpdate = useCallback((subtaskId, newStatusValue) => {
      const taskId = rawTask?.id;
      if (!taskId) return;

      // 1. Optimistic Update for immediate UI feedback
      setSubtasks(prev => prev.map(s => s.id === subtaskId ? { ...s, status: newStatusValue } : s));

      // 2. Refresh full task context to ensure overall status/timeline is synced
      // We don't show the full-screen loader here to keep it feeling snappy
      const isGrouped = checkIsGrouped(rawTask);
      refreshTaskData(taskId, isGrouped, false);

      // 3. Notify parent (e.g., Task.js table) with enough info for optimistic update
      onStatusSuccess?.(`Status updated successfully!`, newStatusValue, subtaskId);

      if (isCompletedStatus(newStatusValue)) {
        setActiveTab("worklog");
        setWorkLogOpenSignal((signal) => signal + 1);
      }
    }, [rawTask, refreshTaskData, onStatusSuccess]);

    const promptWorkLogIfCompleted = useCallback((newStatus) => {
      if (!isCompletedStatus(newStatus)) return;
      setActiveTab("worklog");
      setWorkLogOpenSignal((signal) => signal + 1);
    }, []);


    const handleMemberToggle = useCallback(
      (memberId) =>
        setSelectedMemberId((prev) => (prev === memberId ? null : memberId)),
      []
    );

    /* ── early-exit guard ─────────────────────────────────────────────── */

    if ((!open && variant === "modal") || (!task && !loading && !rawTask)) return null;

    /* ── render ───────────────────────────────────────────────────────── */

    return (
      <div className={`tdm-root tdm-root--${variant} ${refreshing ? 'tdm-is-refreshing' : ''}`}>
        {/* Loader Overlay for sync operations */}
        {refreshing && (
          <div className="tdm-refresh-overlay">
            <CircularProgress size={30} sx={{ color: '#2563eb' }} />
          </div>
        )}
        {/* ── Sticky header ────────────────────────────────────────────── */}
        <div className="tdm-header">
          <div className="tdm-header-left">
            <TicketIdDisplay
              task={rawTask}
              maxVisible={1}
              fontSize="0.78rem"
              clickable={variant !== "full-page"}
            />
          </div>

          <div className="tdm-header-right" style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            {variant === "drawer" && rawTask?.id && (
              <Tooltip title="View Full Page">
                <IconButton
                  size="small"
                  onClick={handleNavigateFullPage}
                  sx={{ color: "#6b7280", "&:hover": { backgroundColor: "#f3f4f6", color: "#111827" } }}
                >
                  <FaExpandAlt size={14} />
                </IconButton>
              </Tooltip>
            )}

            {(variant === "modal" || variant === "drawer") && (
              <button className="tdm-close-btn" onClick={onClose} aria-label="Close">
                <FaTimes size={14} />
              </button>
            )}
          </div>
        </div>

        {/* ── Body ────────────────────────────────────────────────────── */}
        <div className="tdm-body">
          <h2 className="tdm-title">{taskTitle}</h2>

          {/* ── 2-column metadata grid ─────────────────────────────── */}
          <div className="tdm-meta-grid">
            {/* Assigned  To */}
            <div className="tdm-meta-cell">
              <span className="tdm-meta-label">ASSIGNED TO</span>
              <div className="tdm-meta-value tdm-meta-user">
                {displayAssignees.length === 0 ? (
                  <span className="tdm-meta-muted">{META_DASH}</span>
                ) : displayAssignees.length === 1 ? (
                  <UserAvatarTooltip
                    firstName={displayAssignees[0].firstName}
                    lastName={displayAssignees[0].lastName}
                    email={displayAssignees[0].email}
                    displayName={displayAssignees[0].displayName}
                  />
                ) : (
                  <AvatarGroup
                    max={4}
                    sx={{
                      '& .MuiAvatar-root': {
                        width: 28,
                        height: 28,
                        fontSize: '12px',
                        border: '2px solid #fff'
                      }
                    }}
                  >
                    {displayAssignees.map((a, i) => (
                      <UserAvatarTooltip
                        key={a.id || i}
                        firstName={a.firstName}
                        lastName={a.lastName}
                        email={a.email}
                        displayName={a.displayName}
                      />
                    ))}
                  </AvatarGroup>
                )}
              </div>
            </div>

            {/* Assigned By */}
            <div className="tdm-meta-cell">
              <span className="tdm-meta-label">ASSIGNED BY</span>
              <div className="tdm-meta-value tdm-meta-user">
                {givenBy?.displayName ? (
                  <UserAvatarTooltip
                    firstName={givenBy.firstName}
                    lastName={givenBy.lastName}
                    email={givenBy.email}
                    displayName={givenBy.displayName}
                  />
                ) : (
                  <span className="tdm-meta-value tdm-meta-muted">{META_DASH}</span>
                )}
              </div>
            </div>

            {/* Project */}
            <div className="tdm-meta-cell">
              <span className="tdm-meta-label">PROJECT</span>
              {projectName
                ? <span className="tdm-meta-value tdm-project-link">{projectName}</span>
                : <span className="tdm-meta-value tdm-meta-muted">{META_DASH}</span>
              }
            </div>

            {/* Created Date */}
            <div className="tdm-meta-cell">
              <span className="tdm-meta-label">CREATED DATE</span>
              <span className="tdm-meta-value">
                {formatDate(rawTask?.created_at || rawTask?.createdAt) || META_DASH}
              </span>
            </div>

            {/* Start Date */}
            <div className="tdm-meta-cell">
              <span className="tdm-meta-label">START DATE</span>
              <span className="tdm-meta-value">
                {formatDate(rawTask?.start_date || rawTask?.startDate) || META_DASH}
              </span>
            </div>

            {/* Due Date */}
            <div className="tdm-meta-cell">
              <span className="tdm-meta-label">DUE DATE</span>
              <span className={`tdm-meta-value${!hasDueDate ? " tdm-meta-muted" : ""}`}>
                {hasDueDate ? formatDate(rawTask.due_date) : META_DASH}
              </span>
            </div>

            {/* Priority */}
            <div className="tdm-meta-cell">
              <span className="tdm-meta-label">PRIORITY</span>
              <span className="tdm-meta-value tdm-priority" style={{ color: prioCfg.color }}>
                <span className="tdm-priority-icon">{prioCfg.icon}</span>
                {rawTask?.priority || META_DASH}
              </span>
            </div>

            {/* Status */}
            <div className="tdm-meta-cell">
              <span className="tdm-meta-label">STATUS</span>
              <div className="tdm-meta-value" style={{ display: "flex", alignItems: "center" }}>
                <InlineStatusDropdown
                  task={rawTask}
                  userId={currentUserId}
                  onDelegateClick={onDelegateClick}
                  hideDelegateOption={hideDelegateOption}
                  size="small"
                  isMultiAssign={checkIsGrouped(rawTask) && !(hideDelegateOption && (rawTask?.tasks?.length || 0) < 2)}
                  onSuccess={(msg, newStatus) => {
                    // Optimistic update for main status in local modal state
                    setDetails(prev => prev ? { ...prev, status: newStatus } : prev);

                    onStatusSuccess?.(msg, newStatus, rawTask.id);
                    if (rawTask?.id) {
                      const isGrouped = rawTask?.is_grouped === 1 || rawTask?.is_grouped === "1" || rawTask?.is_grouped === true;
                      refreshTaskData(rawTask.id, isGrouped, false);
                    }
                    promptWorkLogIfCompleted(newStatus);
                  }}

                />
              </div>
            </div>

            {/* Estimated Time */}
            <div className="tdm-meta-cell">
              <span className="tdm-meta-label">ESTIMATED TIME</span>
              {editingEstimated ? (
                <div className="tdm-estimated-edit">
                  <input
                    ref={estimatedInputRef}
                    type="text"
                    className={`tdm-estimated-input${estimatedError ? " tdm-estimated-input--error" : ""}`}
                    placeholder={TIME_SPENT_PLACEHOLDER}
                    value={estimatedDraft}
                    onChange={handleEstimatedDraftChange}
                    onKeyDown={handleEstimatedKeyDown}
                    onBlur={handleEstimatedBlur}
                    disabled={savingEstimated}
                  />
                  {estimatedError ? (
                    <p className="tdm-estimated-error">{estimatedError}</p>
                  ) : (
                    <p className="tdm-estimated-hint">{TIME_SPENT_HINT}</p>
                  )}
                </div>
              ) : (
                <div className="tdm-meta-value tdm-estimated-display">
                  <span>{formatTimeSpent(estimatedSeconds)}</span>
                  {canEditEstimatedTime && (
                    <IconButton
                      size="small"
                      onClick={startEstimatedEdit}
                      aria-label="Edit estimated time"
                      sx={{
                        p: 0.5,
                        color: "#94a3b8",
                        "&:hover": { color: "#2563eb", backgroundColor: "rgba(37, 99, 235, 0.04)" },
                      }}
                    >
                      <FaEdit size={12} />
                    </IconButton>
                  )}
                </div>
              )}
            </div>
            {/* Labels */}
            <div className="tdm-meta-cell tdm-meta-cell--full-width">
              <span className="tdm-meta-label">LABELS</span>
              <div className="tdm-meta-value" style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                {labels && labels.length > 0 ? (
                  labels.map((lbl, idx) => {
                    const color = lbl.color;
                    return (
                      <Chip
                        key={lbl.id || idx}
                        label={lbl.name || lbl.labelName || lbl.label || ''}
                        size="small"
                        sx={{
                          backgroundColor: color ? `${color}22` : '#f1f5f9',
                          color: color || '#64748b',
                          border: 'none',
                          borderRadius: '20px',
                          fontWeight: 400,
                          fontSize: '11px',
                          height: '22px',
                          px: 0.5,
                          borderStyle: 'solid',
                          borderWidth: '1px',
                        }}
                      />
                    );
                  })
                ) : (
                  <span className="tdm-meta-muted">{META_DASH}</span>
                )}
              </div>
            </div>
            {/* Logged Time */}
            <div className="tdm-meta-cell tdm-meta-cell--full-width">
              <span className="tdm-meta-label">LOGGED TIME</span>
              <div className="tdm-meta-value">
                <div className="tdm-logged-time-row">
                  <span className="tdm-logged-time-values">
                    {formatLoggedDuration(logged)} / {formatTimeSpent(estimatedSeconds)}
                  </span>
                  <span className="tdm-logged-time-pct">({percentage}%)</span>
                </div>
                <div className="tdm-logged-time-progress">
                  <div
                    className="tdm-logged-time-bar"
                    style={{
                      width: `${percentage}%`,
                      backgroundColor: percentage >= 100 ? "#ef4444" : "#10b981"
                    }}
                  />
                </div>
              </div>
            </div>
          </div>

          {/* ── Description ─────────────────────────────────────────── */}
          <div className="tdm-section">
            <SectionTitle title="DESCRIPTION" />
            <div className="tdm-description-box">
              <p className="tdm-description-text">
                {rawTask?.description || "No description available."}
              </p>
            </div>
          </div>

          {/* ── Attachments ─────────────────────────────────────────── */}
          {attachmentList.length > 0 && (
            <div className="tdm-section">
              <SectionTitle title={`ATTACHMENTS (${attachmentList.length})`} />
              <TaskAttachmentsPanel attachments={attachmentList} allowDelete={canDeleteAttachments} />
            </div>
          )}

          {/* ── Subordinate Tasks ───────────────────────────────────── */}
          {subtasks.length > 0 && (
            <div className="tdm-section">
              <SectionTitle title={`SUB-TASKS (${subtasks.length})`} />
              {loadingSubtasks ? (
                <div className="tdm-loading"><CircularProgress size={22} /></div>
              ) : (
                <div className="tdm-subtask-list">
                  {subtasks.map((st) => (
                    <SubtaskRow
                      key={st.id}
                      st={st}
                      userId={currentUserId}
                      onNavigate={handleSubtaskNavigate}
                      onStatusSuccess={handleSubtaskStatusUpdate}
                    />
                  ))}
                </div>
              )}
            </div>
          )}

          {/* ── Activity section (Timeline + Work Log) ───────────────── */}
          <div className="tdm-section">
            <SectionTitle title="ACTIVITY" />

            {/* Tab headers */}
            <div className="tdm-tabs">
              <button
                className={`tdm-tab${activeTab === "timeline" ? " tdm-tab--active" : ""}`}
                onClick={() => setActiveTab("timeline")}
              >
                Timeline
              </button>
              <button
                className={`tdm-tab${activeTab === "worklog" ? " tdm-tab--active" : ""}`}
                onClick={() => setActiveTab("worklog")}
              >
                Work Log
              </button>
            </div>

            {/* Tab content */}
            {activeTab === "timeline" && (
              <div className="tdm-tab-content">
                <ActivityTimelinePanel timeline={timeline} loadingTimeline={loadingTimeline} />
              </div>
            )}

            {activeTab === "worklog" && (
              <div className="tdm-tab-content">
                <WorkLogPanel
                  taskId={rawTask?.id}
                  estimatedMinutes={estimated}
                  loggedMinutes={logged}
                  taskCode={taskId}
                  openLogModalSignal={workLogOpenSignal + externalWorkLogOpenSignal}
                  onWorklogChange={() => {
                    if (rawTask?.id) {
                      refreshTaskData(
                        rawTask.id,
                        checkIsGrouped(rawTask),
                        false
                      );
                    }
                  }}
                />
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }
);

TaskDetailsModal.displayName = "TaskDetailsModal";

export default TaskDetailsModal;
